import { useState } from "react";
import { Button, CodeBlock, PageHeader, PageSection, Segmented } from "@anyseal/ui";
import { Check, Copy } from "lucide-react";
import { useI18n } from "../i18n";

export function DocsPage() {
  const { locale } = useI18n();
  const [protocol, setProtocol] = useState("openai");
  const [copied, setCopied] = useState(false);

  const samples: Record<string, string> = {
    openai:
      `import OpenAI from "openai";\n\n` +
      `const client = new OpenAI({\n` +
      `  apiKey: process.env.LONMAX_API_KEY,\n` +
      `  baseURL: "https://api.lonmaxcloud.com/v1",\n` +
      `});\n\n` +
      `const result = await client.chat.completions.create({\n` +
      `  model: "your-model-id",\n` +
      `  messages: [{ role: "user", content: "Hello" }],\n` +
      `});`,
    anthropic:
      `import Anthropic from "@anthropic-ai/sdk";\n\n` +
      `const client = new Anthropic({\n` +
      `  apiKey: process.env.LONMAX_API_KEY,\n` +
      `  baseURL: "https://api.lonmaxcloud.com",\n` +
      `});\n\n` +
      `const result = await client.messages.create({\n` +
      `  model: "your-model-id", max_tokens: 1024,\n` +
      `  messages: [{ role: "user", content: "Hello" }],\n` +
      `});`,
    gemini:
      `curl "https://api.lonmaxcloud.com/v1beta/models/your-model-id:generateContent" \\\n` +
      `  -H "Authorization: Bearer $LONMAX_API_KEY" \\\n` +
      `  -H "Content-Type: application/json" \\\n` +
      `  -d '{"contents":[{"parts":[{"text":"Hello"}]}]}'`,
  };

  const sample = samples[protocol] ?? "";

  return (
    <div className="portal-page-grid">
      <PageHeader
        eyebrow={locale === "zh-CN" ? "工作区" : "Workspace"}
        title={locale === "zh-CN" ? "开发文档" : "Developer docs"}
        description={
          locale === "zh-CN"
            ? "使用熟悉的 SDK 接入统一接口；每个响应头都会返回 LonMax AI 请求 ID。"
            : "Use familiar SDKs against the unified API. Every response includes a LonMax AI request ID header."
        }
      />

      <div className="portal-doc-layout">
        <nav
          className="portal-doc-toc"
          aria-label={locale === "zh-CN" ? "文档目录" : "Documentation table of contents"}
        >
          <div className="portal-doc-toc-title">
            {locale === "zh-CN" ? "本页内容" : "On this page"}
          </div>
          <a href="#quickstart">{locale === "zh-CN" ? "快速开始" : "Quickstart"}</a>
          <a href="#authentication">{locale === "zh-CN" ? "鉴权" : "Authentication"}</a>
          <a href="#protocols">{locale === "zh-CN" ? "协议" : "Protocols"}</a>
          <a href="#errors">{locale === "zh-CN" ? "错误处理" : "Errors"}</a>
          <a href="#production">{locale === "zh-CN" ? "生产建议" : "Production"}</a>
        </nav>

        <article className="portal-doc-content">
          <section id="quickstart">
            <PageSection
              title={locale === "zh-CN" ? "五分钟完成首次调用" : "Make your first call in five minutes"}
              description={
                locale === "zh-CN"
                  ? "先在 API 密钥页创建只允许开发模型的密钥，把它保存为服务端环境变量。然后选择与现有代码最接近的协议。"
                  : "Create a development-only key on the API keys page and store it as a server-side environment variable. Then select the protocol closest to your existing code."
              }
            >
              <Segmented
                label={locale === "zh-CN" ? "协议示例" : "Protocol example"}
                value={protocol}
                onChange={setProtocol}
                options={[
                  { value: "openai", label: "OpenAI" },
                  { value: "anthropic", label: "Anthropic" },
                  { value: "gemini", label: "Gemini" },
                ]}
              />
              <div style={{ marginTop: 12 }}>
                <CodeBlock
                  label={protocol === "gemini" ? "cURL" : "TypeScript"}
                  action={
                    <Button
                      variant="quiet"
                      size="small"
                      onClick={() => {
                        void navigator.clipboard.writeText(sample);
                        setCopied(true);
                        setTimeout(() => setCopied(false), 1500);
                      }}
                    >
                      {copied ? <Check size={14} /> : <Copy size={14} />}
                      {copied
                        ? (locale === "zh-CN" ? "已复制" : "Copied")
                        : (locale === "zh-CN" ? "复制" : "Copy")}
                    </Button>
                  }
                >
                  {sample}
                </CodeBlock>
              </div>
            </PageSection>
          </section>

          <section id="authentication">
            <PageSection
              title={locale === "zh-CN" ? "鉴权与秘密管理" : "Authentication and secret handling"}
              description={
                locale === "zh-CN"
                  ? "使用 Authorization: Bearer 发送项目密钥。不要在浏览器、移动应用包或公开仓库中嵌入密钥；客户端应用应调用你自己的服务端。"
                  : "Send the project key with Authorization: Bearer. Never embed keys in browsers, mobile bundles, or public repositories; client applications should call your own server."
              }
            />
          </section>

          <section id="protocols">
            <PageSection
              title={locale === "zh-CN" ? "首发协议范围" : "Launch protocol coverage"}
            >
              <div className="portal-model-grid">
                <DocCard
                  title="OpenAI"
                  text={
                    locale === "zh-CN"
                      ? "Chat、Completions、Responses、Embedding、Images、Audio、Realtime 与 Moderations。"
                      : "Chat, Completions, Responses, Embeddings, Images, Audio, Realtime, and Moderations."
                  }
                />
                <DocCard
                  title="Anthropic"
                  text={
                    locale === "zh-CN"
                      ? "Messages、流式响应、工具调用与兼容转换。"
                      : "Messages, streaming, tool use, and compatible conversions."
                  }
                />
                <DocCard
                  title="Gemini"
                  text={
                    locale === "zh-CN"
                      ? "原生 v1beta 生成、嵌入、流式与协议转换。"
                      : "Native v1beta generation, embeddings, streaming, and conversions."
                  }
                />
              </div>
            </PageSection>
          </section>

          <section id="errors">
            <PageSection
              title={locale === "zh-CN" ? "错误与排障" : "Errors and troubleshooting"}
              description={
                locale === "zh-CN"
                  ? "始终记录响应头 X-Request-ID，但不要记录 Authorization、提示词或完整响应。失败后先判断错误属于输入、额度、限流、平台还是上游，再决定是否重试。"
                  : "Always record the X-Request-ID response header, but never log Authorization, prompts, or full responses. Classify failures as input, credit, rate limit, platform, or upstream before retrying."
              }
            >
              <CodeBlock label="Response headers">{`X-Request-ID: asrq_019...\nContent-Type: application/json`}</CodeBlock>
            </PageSection>
          </section>

          <section id="production">
            <PageSection
              title={locale === "zh-CN" ? "进入生产前" : "Before production"}
            >
              <ul className="portal-doc-list">
                <li>
                  {locale === "zh-CN"
                    ? "为开发、预发布和生产创建不同项目密钥。"
                    : "Use separate project keys for development, staging, and production."}
                </li>
                <li>
                  {locale === "zh-CN"
                    ? "设置模型白名单、CIDR、速率和额度限制。"
                    : "Set model allowlists, CIDRs, rate limits, and quotas."}
                </li>
                <li>
                  {locale === "zh-CN"
                    ? "仅对幂等或可安全恢复的请求实施有限重试。"
                    : "Retry only idempotent or safely recoverable requests, with a strict limit."}
                </li>
                <li>
                  {locale === "zh-CN"
                    ? "在支持工单中只提供请求 ID 和安全诊断摘要。"
                    : "Include only the request ID and safe diagnostic summary in support tickets."}
                </li>
              </ul>
            </PageSection>
          </section>
        </article>
      </div>
    </div>
  );
}

function DocCard({ title, text }: { title: string; text: string }) {
  return (
    <div className="portal-doc-card">
      <h3>{title}</h3>
      <p>{text}</p>
    </div>
  );
}
